'use client';

import {
  BodyStrong,
  Button,
  MenuWithItems,
  MetaDefault,
  type MenuItemProps,
} from '@terra/ui-design-system';
import { HStack, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, type TerraIcon } from '@terra/ui-icons';

export type ComposerPillTone = 'gray' | 'yellow' | 'green' | 'blue' | 'red';

export type ComposerPillOption<TValue extends string> = {
  value: TValue;
  label: string;
  description?: string;
  icon: TerraIcon;
  tone: ComposerPillTone;
};

type ComposerPillMenuProps<TValue extends string> = {
  ariaLabel: string;
  options: ReadonlyArray<ComposerPillOption<TValue>>;
  value: TValue;
  onValueChange: (value: TValue) => void;
  triggerOption?: ComposerPillOption<TValue>;
  extraItems?: MenuItemProps[];
  contentWidth?: string;
};

const TONE_BG: Record<ComposerPillTone, string> = {
  gray: 'bg.decorative.gray',
  yellow: 'bg.decorative.yellow',
  green: 'bg.decorative.green',
  blue: 'bg.decorative.blue',
  red: 'bg.decorative.red',
};

const TONE_FG: Record<ComposerPillTone, string> = {
  gray: 'fg.decorative.gray',
  yellow: 'fg.decorative.yellow',
  green: 'fg.decorative.green',
  blue: 'fg.decorative.blue',
  red: 'fg.decorative.red',
};

export function ComposerPillMenu<TValue extends string>({
  ariaLabel,
  options,
  value,
  onValueChange,
  triggerOption,
  extraItems = [],
  contentWidth,
}: ComposerPillMenuProps<TValue>) {
  const current =
    options.find((option) => option.value === value) ?? triggerOption;

  if (!current) {
    return null;
  }

  const optionItems: MenuItemProps[] = options.map((option, index) => {
    const Icon = option.icon;

    return {
      value: option.value,
      selected: option.value === value,
      onClick: () => onValueChange(option.value),
      withSeparator: extraItems.length > 0 && index === options.length - 1,
      label: (
        <VStack gap={0.5} alignItems="flex-start" minW={0}>
          <HStack gap={1.5} alignItems="center" color={TONE_FG[option.tone]}>
            <Icon size={14} />
            <BodyStrong color={TONE_FG[option.tone]}>{option.label}</BodyStrong>
          </HStack>
          {option.description ? (
            <MetaDefault color="fg.muted" whiteSpace="normal">
              {option.description}
            </MetaDefault>
          ) : null}
        </VStack>
      ),
    };
  });
  const CurrentIcon = current.icon;

  return (
    <MenuWithItems
      items={[...optionItems, ...extraItems]}
      contentWidth={contentWidth}
      positioning={{ strategy: 'fixed', placement: 'top-start' }}
      trigger={
        <Button
          variant="ghost"
          size="2xs"
          aria-label={ariaLabel}
          borderRadius="full"
          px="2.5"
          boxShadow="none"
          bg={TONE_BG[current.tone]}
          color={TONE_FG[current.tone]}
          _hover={{ bg: TONE_BG[current.tone], color: TONE_FG[current.tone] }}
        >
          <CurrentIcon size={14} />
          {current.label}
          <ArrowDown01Icon size={14} />
        </Button>
      }
    />
  );
}
